/**
 * Thin WebRTC wrapper for 1:1 voice calls. Signaling (offer/answer/ICE) is
 * carried over the existing MLS-encrypted `call-signal` channel — see
 * `signaling.ts` and `messaging.ts`'s `sendCallSignal`/`onCallSignal`. This
 * module only ever touches the browser's WebRTC/media APIs.
 */
import { api } from "../lib/api.js";

const PUBLIC_STUN: RTCIceServer = { urls: "stun:stun.l.google.com:19302" };

/** What a call falls back to when TURN credentials can't be had. */
export const STUN_ONLY: RTCIceServer[] = [PUBLIC_STUN];

/** Public STUN plus, when the relay has one configured, short-lived TURN creds. */
export async function getIceServers(creds: { userId: string; token: string }): Promise<RTCIceServer[]> {
  try {
    const { iceServers } = await api.turnCredentials(creds);
    return [PUBLIC_STUN, ...iceServers];
  } catch {
    return [PUBLIC_STUN];
  }
}

/**
 * Open the microphone (and camera only for a video call). The three audio
 * flags default to on in every browser, but saying so explicitly keeps echo
 * cancellation in place on the iPhone loudspeaker route, where the remote
 * voice is played through Web Audio rather than the WebRTC playback path.
 */
export function getLocalStream(video: boolean): Promise<MediaStream> {
  return navigator.mediaDevices.getUserMedia({
    audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    video: video ? { facingMode: "user" } : false,
  });
}

/** Microphone only, for recording a voice message. */
export function getMicStream(): Promise<MediaStream> {
  return navigator.mediaDevices.getUserMedia({
    audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    video: false,
  });
}

/** Release a stream's devices — the iOS recording indicator stays lit otherwise. */
export function stopStream(stream: MediaStream | null | undefined): void {
  for (const track of stream?.getTracks() ?? []) track.stop();
}

export interface CallConnectionHandlers {
  onIceCandidate(candidate: RTCIceCandidateInit): void;
  onRemoteStream(stream: MediaStream): void;
  /** Fires on "connected", "failed", "disconnected", "closed". */
  onStateChange(state: RTCPeerConnectionState): void;
}

/** One WebRTC peer connection for the lifetime of a single call. */
export class CallConnection {
  private readonly pc: RTCPeerConnection;
  private readonly remoteStream = new MediaStream();
  /** ICE candidates that arrive before the remote description is set. */
  private readonly bufferedCandidates: RTCIceCandidateInit[] = [];
  private remoteDescriptionSet = false;

  constructor(iceServers: RTCIceServer[], handlers: CallConnectionHandlers) {
    this.pc = new RTCPeerConnection({ iceServers });

    this.pc.addEventListener("icecandidate", (e) => {
      if (e.candidate) handlers.onIceCandidate(e.candidate.toJSON());
    });
    this.pc.addEventListener("track", (e) => {
      this.remoteStream.addTrack(e.track);
      handlers.onRemoteStream(this.remoteStream);
    });
    this.pc.addEventListener("connectionstatechange", () => {
      handlers.onStateChange(this.pc.connectionState);
    });
  }

  addLocalStream(stream: MediaStream): void {
    for (const track of stream.getTracks()) this.pc.addTrack(track, stream);
  }

  async createOffer(): Promise<RTCSessionDescriptionInit> {
    const offer = await this.pc.createOffer();
    await this.pc.setLocalDescription(offer);
    return offer;
  }

  /** Callee side: apply the caller's offer, produce our answer. */
  async createAnswer(offer: RTCSessionDescriptionInit): Promise<RTCSessionDescriptionInit> {
    await this.pc.setRemoteDescription(offer);
    await this.flushBufferedCandidates();
    const answer = await this.pc.createAnswer();
    await this.pc.setLocalDescription(answer);
    return answer;
  }

  /** Caller side: apply the callee's answer. */
  async applyAnswer(answer: RTCSessionDescriptionInit): Promise<void> {
    await this.pc.setRemoteDescription(answer);
    await this.flushBufferedCandidates();
  }

  /** "stable" means no offer/answer exchange is outstanding. */
  get signalingState(): RTCSignalingState {
    return this.pc.signalingState;
  }

  get state(): RTCPeerConnectionState {
    return this.pc.connectionState;
  }

  /** Caller side: a new offer that makes both ends gather fresh network paths. */
  async createRestartOffer(): Promise<RTCSessionDescriptionInit> {
    const offer = await this.pc.createOffer({ iceRestart: true });
    await this.pc.setLocalDescription(offer);
    return offer;
  }

  /** Callee side: accept the caller's ICE-restart offer and answer it. */
  async answerRestart(offer: RTCSessionDescriptionInit): Promise<RTCSessionDescriptionInit> {
    await this.pc.setRemoteDescription(offer);
    const answer = await this.pc.createAnswer();
    await this.pc.setLocalDescription(answer);
    return answer;
  }

  /**
   * Swap in new microphone/camera tracks without renegotiating — used when the
   * OS ended the old ones while the phone was locked.
   */
  async replaceLocalTracks(stream: MediaStream): Promise<void> {
    for (const sender of this.pc.getSenders()) {
      const kind = sender.track?.kind;
      const next = stream.getTracks().find((t) => t.kind === kind);
      if (next) await sender.replaceTrack(next);
    }
  }

  async addRemoteIceCandidate(candidate: RTCIceCandidateInit): Promise<void> {
    if (!this.remoteDescriptionSet) {
      this.bufferedCandidates.push(candidate);
      return;
    }
    await this.pc.addIceCandidate(candidate);
  }

  setMuted(muted: boolean, localStream: MediaStream): void {
    for (const track of localStream.getAudioTracks()) track.enabled = !muted;
  }

  setVideoEnabled(enabled: boolean, localStream: MediaStream): void {
    for (const track of localStream.getVideoTracks()) track.enabled = enabled;
  }

  close(): void {
    this.pc.close();
  }

  private async flushBufferedCandidates(): Promise<void> {
    this.remoteDescriptionSet = true;
    const queued = this.bufferedCandidates.splice(0);
    for (const c of queued) await this.pc.addIceCandidate(c);
  }
}
